<template>
  <div class="platform-content-wrapper">
    <div class="platform-header-info">
      <div class="platform-icon-large" :class="platform.gradient">
        <slot name="icon" :size="32" :isFill="true" color="white"></slot>
      </div>
      <div class="platform-details">
        <h2>{{ $t(titleKey) }}</h2>
        <p>{{ $t(descKey) }}</p>
      </div>
      <div class="platform-stats">
        <div class="stat-item">
          <span class="stat-label-1">{{ $t('_Configurations') }}: {{ activeCount }}</span>
        </div>
      </div>
    </div>
    <v-row justify="end" class="mb-4 mr-2">
      <v-btn
        color="success"
        size="large"
        variant="flat"
        @click="onSave"
        :loading="busy"
        :disabled="busy"
        prepend-icon="mdi-content-save"
        class="save-action-button-small mr-3"
        rounded="lg"
        :data-testid="`commerce-save-${platform.key}`"
      >
        <template v-slot:prepend>
          <v-img :src="images.saveImg" width="20" height="20"></v-img>
        </template>
        {{ $t('_SaveChanges') }}
      </v-btn>
      <v-btn color="success" variant="flat" size="default" @click="$emit('add')" rounded="lg" class="outlined-red-btn" :data-testid="`commerce-add-${platform.key}`">
        <v-icon size="18" class="mr-2">mdi-plus</v-icon>
        {{ $t('_AddConfiguration') }}
      </v-btn>
    </v-row>

    <div v-if="ready" class="configurations-grid">
      <!-- Empty state -->
      <div v-if="activeCount === 0" class="empty-state-card" :data-testid="`commerce-empty-${platform.key}`">
        <div class="empty-icon">
          <slot name="icon" :size="64" :isFill="false" color="#e0e0e0"></slot>
        </div>
        <h3 class="noitemtitle">{{ $t('_NoCommerceConfig', { platform: platformName }) }}</h3>
        <p class="noitemdesc">{{ $t('_CreateFirstCommerceConfig', { platform: platformName }) }}</p>
        <v-btn color="primary" variant="flat" size="large" @click="$emit('add')" class="mt-4 control-btn create-btn customadd">
          <v-icon left>mdi-plus</v-icon>
          {{ $t('_CreateFirstConfiguration') }}
        </v-btn>
      </div>

      <!-- Configuration cards -->
      <div v-else>
        <v-expansion-panels variant="accordion" class="mb-4" multiple v-model="openPanels">
          <v-expansion-panel
            v-for="(item, index) in items"
            :key="cardId(item, index)"
            :value="cardId(item, index)"
            v-show="item.activeFlag"
            :class="['accordion-custom', { 'accordion-open': openPanels.includes(cardId(item, index)) }]"
            :data-testid="`commerce-card-${cardId(item, index)}`"
            :data-social-id="item.socialID"
          >
            <v-expansion-panel-title class="accordion-title">
              <div class="d-flex align-center">
                <img :src="images.gear" alt="config" class="mr-1" style="width: 20px; height: 20px" />
                <span style="font-weight: 600" v-if="item.name">{{ item.name }}</span>
                <span style="font-weight: 600" v-else>{{ $t('_Config') }} #{{ index + 1 }}</span>
                <!-- U1 — badge สถานะย่อบนหัว accordion (เฉพาะการ์ดที่ Save แล้ว) -->
                <template v-if="item.socialID > 0">
                  <v-tooltip v-if="isCompact" location="top">
                    <template v-slot:activator="{ props }">
                      <v-chip
                        v-bind="props"
                        size="x-small"
                        class="ml-2 commerce-status-dot"
                        :color="statusMeta(connectionStatus(item)).color"
                        variant="flat"
                        :data-testid="`commerce-status-chip-header-${cardId(item, index)}`"
                        :data-status="connectionStatus(item)"
                      >
                        <v-icon size="10">mdi-circle</v-icon>
                      </v-chip>
                    </template>
                    <span>{{ $t(statusMeta(connectionStatus(item)).label) }}</span>
                  </v-tooltip>
                  <v-chip
                    v-else
                    size="x-small"
                    class="ml-2"
                    :color="statusMeta(connectionStatus(item)).color"
                    variant="flat"
                    :prepend-icon="statusMeta(connectionStatus(item)).icon"
                    :data-testid="`commerce-status-chip-header-${cardId(item, index)}`"
                    :data-status="connectionStatus(item)"
                  >
                    {{ $t(statusMeta(connectionStatus(item)).label) }}
                  </v-chip>
                </template>
              </div>
              <v-spacer></v-spacer>
              <v-btn icon size="small" variant="text" @click.stop="$emit('delete', item, index)" class="delete-icon-btn" :data-testid="`commerce-delete-${cardId(item, index)}`">
                <img :src="images.delete" alt="Delete" style="width: 25px; height: 25px" />
              </v-btn>
              <template #actions="{ expanded }">
                <v-icon :icon="expanded ? 'mdi-menu-up' : 'mdi-menu-down'" color="#171717" size="32"></v-icon>
              </template>
            </v-expansion-panel-title>

            <v-expansion-panel-text class="accordion-content">
              <v-card flat class="accordion-inner-card">
                <v-card-text>
                  <!-- Section 1 — Basic Information -->
                  <div class="section">
                    <div class="section-header">
                      <v-img :src="images.basicImg" alt="Basic Information" class="mr-2" style="max-width: 20px; height: 20px"></v-img>
                      <span class="section-title-1">{{ $t('basicInfo') }}</span>
                    </div>

                    <v-row class="mt-3" dense>
                      <v-col cols="12" sm="6" class="col-label-input">
                        <div class="d-flex align-center">
                          <span>{{ $t('_ConfigurationName') }} <span style="color: red">*</span></span>
                          <v-tooltip location="top" max-width="300">
                            <template v-slot:activator="{ props }">
                              <v-icon small color="grey lighten-1" v-bind="props" class="ml-1">mdi-information-outline</v-icon>
                            </template>
                            <span>{{ $t('_TipConfigNameCommerce') }}</span>
                          </v-tooltip>
                        </div>
                        <v-text-field
                          :ref="`name-${index}`"
                          v-model="item.name"
                          :rules="[rules.required, (v) => (v && v.length <= 100) || $t('_Max100Characters')]"
                          @keypress="onNameKeypress($event)"
                          variant="outlined"
                          density="comfortable"
                          hide-details="auto"
                          class="modern-field mt-1"
                          :placeholder="$t('_PlaceholderEnterConfigurationName')"
                          color="primary"
                          :data-testid="`commerce-name-${cardId(item, index)}`"
                        >
                        </v-text-field>
                      </v-col>

                      <v-col cols="12" sm="6" class="col-label-input">
                        <div class="d-flex align-center">
                          <span>{{ $t('modelGroup') }}</span>
                          <v-tooltip location="top" max-width="300">
                            <template v-slot:activator="{ props }">
                              <v-icon small color="grey lighten-1" v-bind="props" class="ml-1">mdi-information-outline</v-icon>
                            </template>
                            <span>{{ $t('_TipModelGroupPredefined') }}</span>
                          </v-tooltip>
                        </div>
                        <v-autocomplete
                          :items="ddGroupModel"
                          item-title="name"
                          item-value="id"
                          v-model="item.groupModelID"
                          clearable
                          variant="outlined"
                          density="comfortable"
                          hide-details="auto"
                          class="modern-field mt-1"
                          :placeholder="$t('_PlaceholderSelectModelGroup')"
                          color="primary"
                          :data-testid="`commerce-groupmodel-${cardId(item, index)}`"
                        >
                        </v-autocomplete>
                      </v-col>
                    </v-row>
                  </div>

                  <div class="action-divider"></div>

                  <!-- Section 2 — Authentication Details -->
                  <div class="section">
                    <div class="section-header">
                      <v-img :src="images.shieldImg" alt="Authentication Details" class="mr-2" style="max-width: 20px; height: 20px"></v-img>
                      <span class="section-title-1">{{ $t('_AuthenticationDetails') }}</span>
                    </div>

                    <v-row class="mt-3" dense>
                      <v-col cols="12" sm="6" class="col-label-input">
                        <div class="d-flex align-center">
                          <span>{{ $t('_AppKey') }} <span style="color: red">*</span></span>
                          <v-tooltip location="top" max-width="300">
                            <template v-slot:activator="{ props }">
                              <v-icon small color="grey lighten-1" v-bind="props" class="ml-1">mdi-information-outline</v-icon>
                            </template>
                            <span>{{ $t('_TipAppKey') }}</span>
                          </v-tooltip>
                        </div>
                        <v-text-field
                          v-model="item.appKey"
                          :rules="[rules.required]"
                          variant="outlined"
                          density="comfortable"
                          hide-details="auto"
                          class="modern-field mt-1"
                          color="primary"
                          :data-testid="`commerce-appkey-${cardId(item, index)}`"
                        >
                        </v-text-field>
                      </v-col>

                      <v-col cols="12" sm="6" class="col-label-input">
                        <div class="d-flex align-center">
                          <span>{{ $t('_AppSecret') }} <span style="color: red">*</span></span>
                          <v-tooltip location="top" max-width="300">
                            <template v-slot:activator="{ props }">
                              <v-icon small color="grey lighten-1" v-bind="props" class="ml-1">mdi-information-outline</v-icon>
                            </template>
                            <span>{{ $t('_TipAppSecret') }}</span>
                          </v-tooltip>
                        </div>
                        <v-text-field
                          v-model="item.secret"
                          :rules="[rules.required]"
                          variant="outlined"
                          density="comfortable"
                          hide-details="auto"
                          :type="isSecretShown(item, index) ? 'text' : 'password'"
                          class="modern-field mt-1"
                          color="primary"
                          :data-testid="`commerce-secret-${cardId(item, index)}`"
                        >
                          <template v-slot:append-inner>
                            <v-btn icon variant="text" size="small" :aria-label="$t('_AppSecret')" @click="toggleSecret(item, index, $event)" :data-testid="`commerce-secret-toggle-${cardId(item, index)}`">
                              <v-icon size="16">{{ isSecretShown(item, index) ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
                            </v-btn>
                          </template>
                        </v-text-field>
                      </v-col>

                      <v-col cols="12" sm="6" class="col-label-input">
                        <div class="d-flex align-center">
                          <span>
                            {{ $t('_ShopId') }}
                            <span v-if="!platform.shopIdReadonly" style="color: red">*</span>
                          </span>
                          <v-tooltip location="top" max-width="300">
                            <template v-slot:activator="{ props }">
                              <v-icon small color="grey lighten-1" v-bind="props" class="ml-1">mdi-information-outline</v-icon>
                            </template>
                            <span>{{ $t('_TipShopId') }}</span>
                          </v-tooltip>
                        </div>
                        <v-text-field
                          v-model="item.shopID"
                          :readonly="platform.shopIdReadonly"
                          :rules="platform.shopIdReadonly ? [] : [rules.required]"
                          variant="outlined"
                          density="comfortable"
                          hide-details="auto"
                          class="modern-field mt-1"
                          :placeholder="platform.shopIdReadonly ? $t('_TipShopId') : ''"
                          color="primary"
                          :data-testid="`commerce-shopid-${cardId(item, index)}`"
                        >
                        </v-text-field>
                      </v-col>

                      <v-col v-if="platform.needsRegion" cols="12" sm="6" class="col-label-input">
                        <div class="d-flex align-center">
                          <span>{{ $t('_Region') }} <span style="color: red">*</span></span>
                          <v-tooltip location="top" max-width="300">
                            <template v-slot:activator="{ props }">
                              <v-icon small color="grey lighten-1" v-bind="props" class="ml-1">mdi-information-outline</v-icon>
                            </template>
                            <span>{{ $t('_TipRegion') }}</span>
                          </v-tooltip>
                        </div>
                        <v-select
                          :items="regions"
                          v-model="item.region"
                          :rules="[rules.required]"
                          variant="outlined"
                          density="comfortable"
                          hide-details="auto"
                          class="modern-field mt-1"
                          color="primary"
                          :data-testid="`commerce-region-${cardId(item, index)}`"
                        >
                        </v-select>
                      </v-col>

                      <!-- T1 — TikTok Shop: ลิงก์ authorize ใช้ service_id · บังคับตอน Connect (ไม่บังคับตอน Save) -->
                      <v-col v-if="platform.needsServiceId" cols="12" sm="6" class="col-label-input">
                        <div class="d-flex align-center">
                          <span>{{ $t('_ServiceId') }} <span style="color: red">*</span></span>
                          <v-tooltip location="top" max-width="300">
                            <template v-slot:activator="{ props }">
                              <v-icon small color="grey lighten-1" v-bind="props" class="ml-1">mdi-information-outline</v-icon>
                            </template>
                            <span>{{ $t('_TipServiceId') }}</span>
                          </v-tooltip>
                        </div>
                        <v-text-field
                          v-model="item.serviceID"
                          variant="outlined"
                          density="comfortable"
                          hide-details="auto"
                          class="modern-field mt-1"
                          color="primary"
                          :data-testid="`commerce-serviceid-${cardId(item, index)}`"
                        >
                        </v-text-field>
                      </v-col>
                    </v-row>
                  </div>

                  <div class="action-divider"></div>

                  <!-- Section 3 — Platform setup links -->
                  <div class="section">
                    <div class="section-header">
                      <v-img :src="images.chainImg" alt="Platform setup links" class="mr-2" style="max-width: 20px; height: 20px"></v-img>
                      <span class="section-title-1">{{ $t('_ProviderLinks') }}</span>
                    </div>

                    <v-row class="mt-3" dense>
                      <v-col cols="12" class="col-label-input">
                        <div class="d-flex align-center">
                          <span>{{ $t('_RedirectUri') }}</span>
                          <v-tooltip location="top" max-width="300">
                            <template v-slot:activator="{ props }">
                              <v-icon small color="grey lighten-1" v-bind="props" class="ml-1">mdi-information-outline</v-icon>
                            </template>
                            <span>{{ $t('_TipRedirectUri') }}</span>
                          </v-tooltip>
                        </div>
                        <v-text-field
                          :model-value="item.redirectUri || ''"
                          readonly
                          variant="outlined"
                          density="comfortable"
                          hide-details="auto"
                          class="modern-field mt-1 commerce-link-field"
                          color="primary"
                          :data-testid="`commerce-redirect-uri-${cardId(item, index)}`"
                        >
                          <template v-slot:append-inner>
                            <v-btn icon variant="text" size="small" :aria-label="$t('_Copy')" :disabled="!item.redirectUri" @click="copy(item.redirectUri, $event)" :data-testid="`commerce-redirect-copy-${cardId(item, index)}`">
                              <v-icon size="16">mdi-content-copy</v-icon>
                            </v-btn>
                          </template>
                        </v-text-field>
                        <div v-if="!item.socialID" class="text-caption text-medium-emphasis mt-1">{{ $t('_ShownAfterSave') }}</div>
                      </v-col>

                      <v-col cols="12" class="col-label-input">
                        <div class="d-flex align-center">
                          <span>{{ $t('_WebhookUrl') }}</span>
                          <v-tooltip location="top" max-width="300">
                            <template v-slot:activator="{ props }">
                              <v-icon small color="grey lighten-1" v-bind="props" class="ml-1">mdi-information-outline</v-icon>
                            </template>
                            <span>{{ $t('_TipWebhookUrl') }}</span>
                          </v-tooltip>
                        </div>
                        <v-text-field
                          :model-value="item.webhookUrl || ''"
                          readonly
                          variant="outlined"
                          density="comfortable"
                          hide-details="auto"
                          class="modern-field mt-1 commerce-link-field"
                          color="primary"
                          :data-testid="`commerce-webhook-url-${cardId(item, index)}`"
                        >
                          <template v-slot:append-inner>
                            <v-btn icon variant="text" size="small" :aria-label="$t('_Copy')" :disabled="!item.webhookUrl" @click="copy(item.webhookUrl, $event)" :data-testid="`commerce-webhook-copy-${cardId(item, index)}`">
                              <v-icon size="16">mdi-content-copy</v-icon>
                            </v-btn>
                          </template>
                        </v-text-field>
                        <div v-if="!item.socialID" class="text-caption text-medium-emphasis mt-1">{{ $t('_ShownAfterSave') }}</div>
                      </v-col>
                    </v-row>
                  </div>

                  <div class="action-divider"></div>

                  <!-- Section 4 — Connection status (C0–C3 · P1-9 · U2) -->
                  <div class="section">
                    <div class="section-header">
                      <v-icon size="20" class="mr-2" color="#374151">mdi-connection</v-icon>
                      <span class="section-title-1">{{ $t('_ConnectionStatus') }}</span>
                    </div>

                    <v-row class="mt-3" dense align="center">
                      <v-col cols="12" md="8" class="col-label-input">
                        <v-chip
                          variant="tonal"
                          :color="statusMeta(connectionStatus(item)).color"
                          :prepend-icon="statusMeta(connectionStatus(item)).icon"
                          :data-testid="`commerce-status-chip-${cardId(item, index)}`"
                          :data-status="connectionStatus(item)"
                        >
                          {{ $t(statusMeta(connectionStatus(item)).label) }}
                        </v-chip>

                        <div v-if="connectionStatus(item) === STATUS.EXPIRING" class="text-body-2 text-warning mt-2">
                          {{ $t('_ExpiringSoonHint', { date: formatDate(item.refreshTokenExpiry) }) }}
                        </div>

                        <div v-if="connectionStatus(item) !== STATUS.NOT_CONNECTED" class="text-body-2 mt-2">
                          <span :data-testid="`commerce-token-expiry-${cardId(item, index)}`">{{ $t('_TokenExpiresAt') }} {{ formatDate(item.tokenExpiry) }}</span>
                          <template v-if="connectionStatus(item) !== STATUS.EXPIRED">
                            <span class="mx-1">·</span>
                            <span :data-testid="`commerce-connected-at-${cardId(item, index)}`">{{ $t('_ConnectedAt') }} {{ formatDate(item.connectedDate) }}</span>
                          </template>
                        </div>

                        <template v-if="connectionStatus(item) === STATUS.CONNECTED || connectionStatus(item) === STATUS.EXPIRING">
                          <div class="text-body-2 text-medium-emphasis mt-1">{{ $t('_AutoRefreshNote') }}</div>
                          <div class="text-caption text-medium-emphasis mt-1" :data-testid="`commerce-status-hint-${cardId(item, index)}`">
                            <v-icon size="14" class="mr-1">mdi-information-outline</v-icon>{{ $t('_ConnectedStatusHint') }}
                          </div>
                        </template>
                      </v-col>

                      <v-col cols="12" md="4" class="d-flex justify-end col-label-input">
                        <v-tooltip location="top" :disabled="canConnect(item)">
                          <template v-slot:activator="{ props }">
                            <span v-bind="props" :class="{ 'w-100': isCompact }" :data-testid="`commerce-connect-wrap-${cardId(item, index)}`">
                              <v-btn
                                :color="statusMeta(connectionStatus(item)).btnColor"
                                :variant="statusMeta(connectionStatus(item)).btnVariant"
                                :disabled="busy || !canConnect(item)"
                                :loading="busy"
                                :block="isCompact"
                                size="large"
                                rounded="lg"
                                class="commerce-connect-btn"
                                @click="onConnect(item)"
                                :data-testid="`commerce-connect-${cardId(item, index)}`"
                              >
                                <v-icon size="18" class="mr-2">mdi-link-variant</v-icon>
                                {{ $t(connectionStatus(item) === STATUS.NOT_CONNECTED ? '_Connect' : '_Reconnect') }}
                              </v-btn>
                            </span>
                          </template>
                          <span>{{ $t(connectBlockedHint(item)) }}</span>
                        </v-tooltip>
                      </v-col>
                    </v-row>
                  </div>
                </v-card-text>
              </v-card>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
    </div>
  </div>
</template>

<script>
// S14-P1 (P1-W4-A) — แถบ commerce (TikTok Shop / Shopee / Lazada) ใช้ component เดียวกัน 3 แถบ (P1-8 — ไม่ copy template Instagram ×3)
// สถานะ C0–C3 คำนวณจาก connectedDate / tokenExpiry / refreshTokenExpiry (P1-9 · U2 · D9 — ไม่มีคอลัมน์ status)
const EXPIRING_SOON_MS = 3 * 24 * 60 * 60 * 1000; // TS-1 — เกณฑ์ "ใกล้หมดอายุ" 3 วัน (ปรับหลัง manual TC-08–10)
const STATUS = Object.freeze({
  NOT_CONNECTED: 'not_connected',
  CONNECTED: 'connected',
  EXPIRING: 'expiring',
  EXPIRED: 'expired',
});
const TITLE_KEYS = Object.freeze({ tiktok: '_TikTokShop', shopee: '_Shopee', lazada: '_Lazada' });
const DESC_KEYS = Object.freeze({ tiktok: '_DescTikTok', shopee: '_DescShopee', lazada: '_DescLazada' });
const STATUS_META = Object.freeze({
  [STATUS.NOT_CONNECTED]: { color: 'grey', icon: 'mdi-link-off', label: '_StatusNotConnected', btnColor: 'primary', btnVariant: 'flat' },
  [STATUS.CONNECTED]: { color: 'success', icon: 'mdi-check-circle', label: '_StatusConnected', btnColor: 'primary', btnVariant: 'outlined' },
  [STATUS.EXPIRING]: { color: 'warning', icon: 'mdi-alert-circle', label: '_StatusExpiringSoon', btnColor: 'warning', btnVariant: 'flat' },
  [STATUS.EXPIRED]: { color: 'error', icon: 'mdi-alert-octagon', label: '_StatusReconnectRequired', btnColor: 'error', btnVariant: 'flat' },
});

// API ส่ง DateTime UTC โดยไม่มี offset (เช่น 2026-09-17T08:33:25) → ต่อ 'Z' ก่อน parse (pattern FlowList.vue) — ALL-W7-A TC-07b
function toUtcDate(value) {
  if (!value) return null;
  const s = String(value);
  const needsZ = s.includes('T') && !s.includes('Z') && !/[+-]\d{2}:\d{2}$/.test(s);
  return new Date(needsZ ? s + 'Z' : s);
}
export default {
  name: 'CommerceChannelTab',
  props: {
    platform: { type: Object, required: true }, // { key, name, needsRegion, shopIdReadonly, needsServiceId, chipColor, gradient }
    items: { type: Array, required: true }, // setupListCommerce[platform.key] — reactive · แก้ item ตรง ๆ เหมือนแถบเดิม
    ddGroupModel: { type: Array, default: () => [] },
    regions: { type: Array, default: () => [] }, // lazadaRegions — ใช้เฉพาะ platform.needsRegion (RC3)
    busy: { type: Boolean, default: false }, // = parent.dialog
    images: { type: Object, required: true }, // saveImg / basicImg / shieldImg / chainImg / gear / delete ของ parent
    ready: { type: Boolean, default: true }, // = parent.chkDataLine
    specialCharacters: { type: Function, default: null }, // method เดิมของ parent (@keypress ช่องชื่อ)
  },
  emits: ['add', 'delete', 'connect', 'save'],
  data: () => ({
    STATUS,
    showSecretKeys: [],
    openPanels: [],
  }),
  computed: {
    rules() {
      return {
        required: (value) => !!value || this.$t('_Required'),
      };
    },
    titleKey() {
      return TITLE_KEYS[this.platform.key] ?? '_Config';
    },
    descKey() {
      return DESC_KEYS[this.platform.key] ?? '_Config';
    },
    platformName() {
      return this.platform.name ?? this.$t(this.titleKey);
    },
    activeCount() {
      return this.items.filter((i) => i.activeFlag).length;
    },
    isCompact() {
      return this.$vuetify?.display?.smAndDown ?? false;
    },
  },
  watch: {
    'items.length'(n, o) {
      // กด Add → การ์ดใหม่กาง + autofocus ช่องชื่อ (U6 / journey ข้อ 2)
      if (n > o) {
        const idx = n - 1;
        const id = this.cardId(this.items[idx], idx);
        if (!this.openPanels.includes(id)) this.openPanels.push(id);
        this.$nextTick(() => {
          const ref = this.$refs[`name-${idx}`];
          const field = Array.isArray(ref) ? ref[0] : ref;
          field?.focus?.();
        });
      }
    },
  },
  methods: {
    connectionStatus(item) {
      // P1-9 + U2 · TS-2: refreshTokenExpiry null = เขียวปกติ
      if (!item.connectedDate) return STATUS.NOT_CONNECTED;
      const now = Date.now();
      const tokenAt = item.tokenExpiry ? toUtcDate(item.tokenExpiry).getTime() : null;
      const refreshAt = item.refreshTokenExpiry ? toUtcDate(item.refreshTokenExpiry).getTime() : null;
      if ((tokenAt !== null && tokenAt < now) || (refreshAt !== null && refreshAt < now)) return STATUS.EXPIRED;
      if (refreshAt !== null && refreshAt - now < EXPIRING_SOON_MS) return STATUS.EXPIRING;
      return STATUS.CONNECTED;
    },
    statusMeta(status) {
      return STATUS_META[status] ?? STATUS_META[STATUS.NOT_CONNECTED];
    },
    canConnect(item) {
      // P1-5 — guard เดียวกับ :disabled ของปุ่ม Connect (needsRegion = true เฉพาะ lazada · RC3 · needsServiceId = true เฉพาะ tiktok · T1)
      return item.socialID > 0 && !!item.appKey && !!item.secret && (!this.platform.needsRegion || !!item.region) && (!this.platform.needsServiceId || !!item.serviceID?.trim());
    },
    connectBlockedHint(item) {
      // T1 — Save แล้วแต่ยังไม่มี Service ID → บอกให้กรอก Service ID แทนข้อความ "บันทึกก่อน"
      if (item.socialID > 0 && this.platform.needsServiceId && !item.serviceID?.trim()) return '_ServiceIdBeforeConnect';
      return '_SaveBeforeConnect';
    },
    onConnect(item) {
      if (!this.busy && this.canConnect(item)) this.$emit('connect', item);
    },
    onSave() {
      if (!this.busy) this.$emit('save');
    },
    onNameKeypress(evt) {
      if (typeof this.specialCharacters === 'function') this.specialCharacters(evt);
    },
    cardId(item, index) {
      return item.socialID > 0 ? String(item.socialID) : `new-${index}`;
    },
    secretKey(item, index) {
      // ไม่ใช้ค่า secret เป็น key (ของเดิม :1327 key เปลี่ยนทุกครั้งที่พิมพ์)
      return `${this.platform.key}secret:${item.socialID}:${index}`;
    },
    isSecretShown(item, index) {
      return this.showSecretKeys.includes(this.secretKey(item, index));
    },
    toggleSecret(item, index, ev) {
      ev?.preventDefault?.();
      ev?.stopPropagation?.();
      const key = this.secretKey(item, index);
      const i = this.showSecretKeys.indexOf(key);
      if (i >= 0) {
        this.showSecretKeys.splice(i, 1);
      } else {
        this.showSecretKeys.push(key);
      }
    },
    async copy(text, ev) {
      // U6 — feedback ทุกครั้ง · ไม่มี clipboard (http ที่ไม่ใช่ localhost) → select ให้กด Ctrl+C เอง
      if (!text) return;
      const input = ev?.target?.closest?.('.v-field')?.querySelector?.('input');
      try {
        await navigator.clipboard.writeText(text);
        this.$swal.fire({ toast: true, position: 'top-end', icon: 'success', title: this.$t('copied'), timer: 1200, showConfirmButton: false });
      } catch {
        input?.select?.();
      }
    },
    formatDate(value) {
      if (!value) return '';
      const date = toUtcDate(value);
      if (!date || Number.isNaN(date.getTime())) return '';
      const locale = this.$i18n?.locale === 'th' ? 'th-TH' : 'en-GB';
      return date.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
    },
  },
};
</script>

<style scoped>
/* scoped CSS ของ SetupSocial.vue ไม่ตกทอดมาถึง child — คัดเฉพาะ class ที่ใช้ (ค่าเดียวกับ SetupSocial.vue) */
.platform-content-wrapper {
  padding: 32px;
}

.platform-header-info {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 16px;
  margin-bottom: 32px;
  border: 1px solid rgba(226, 232, 240, 0.8);
}

.platform-icon-large {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.platform-details {
  flex: 1;
}

.platform-details h2 {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px 0;
}

.platform-details p {
  color: #6b7280;
  font-size: 1rem;
  margin: 0;
}

.platform-stats {
  display: flex;
  gap: 24px;
}

.stat-item {
  text-align: center;
  padding: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  min-width: 80px;
}

.stat-label-1 {
  display: block;
  font-size: 16px;
  color: #171717;
  letter-spacing: 0.5px;
  margin-top: 4px;
}

/* พื้นหลังขาว — ไอคอนแบบ isFill ใช้สีแบรนด์อยู่แล้ว ถ้าพื้นหลังเป็นสีแบรนด์เดียวกันไอคอนจะกลืนจนมองไม่เห็น */
.tiktok-gradient {
  background: #ffffff;
}

.shopee-gradient {
  background: #ffffff;
}

.lazada-gradient {
  background: #ffffff;
}

.configurations-grid {
  min-height: 400px;
}

.empty-state-card {
  background: white;
  border-radius: 16px;
  padding: 64px 32px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid #f1f5f9;
}

.empty-icon {
  margin-bottom: 24px;
}

.empty-state-card h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px 0;
}

.empty-state-card p {
  color: #6b7280;
  font-size: 1rem;
  margin: 0 0 32px 0;
}

.noitemtitle {
  font-size: 16px !important;
  font-weight: 600;
  color: #646464 !important;
  margin: 0 0 8px 0;
}

.noitemdesc {
  font-size: 16px !important;
  color: #646464 !important;
  margin: 0;
}

.delete-icon-btn {
  transition: all 0.3s ease;
}

.delete-icon-btn:hover {
  background: rgba(239, 68, 68, 0.1) !important;
  transform: scale(1.1);
}

.modern-field :deep(.v-field) {
  background: #fafbfc;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.modern-field :deep(.v-field:hover) {
  background: #f8fafc;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.modern-field :deep(.v-field--focused) {
  background: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
}

.commerce-link-field :deep(input) {
  text-overflow: ellipsis;
}

.outlined-red-btn {
  border-radius: 12px !important;
  width: 180px;
  height: 40px;
  color: #c32e0e !important;
  font-size: 13px !important;
  background-color: transparent !important;
  border: 1px solid #c32e0e !important;
  text-transform: none !important;
}

.save-action-button-small {
  min-width: 180px !important;
  height: 40px !important;
  font-size: 1rem !important;
  font-weight: 600 !important;
  text-transform: none !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  border-radius: 12px !important;
}

.accordion-custom {
  background: linear-gradient(90deg, #fff2f2 0%, #ffded8 100%);
  border-radius: 12px;
  border: 1px solid #f5c2c2;
  overflow: hidden;
  transition: all 0.3s ease;
  margin-bottom: 10px;
}

.accordion-title {
  padding: 12px 20px;
  font-weight: 500;
  transition: all 0.3s ease;
  border-bottom: 1px solid #f1c7c7;
  border-radius: 12px 12px 0 0;
}

.accordion-open {
  border-bottom: 1px solid #f1c7c7;
  border-radius: 12px;
}

.accordion-content {
  background-color: white;
  border-radius: 0 0 12px 12px;
  transition: all 0.3s ease;
}

.accordion-inner-card {
  background-color: transparent;
  box-shadow: none;
}

.section {
  margin-bottom: 16px;
}

.section-header {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #333;
}

.section-title-1 {
  font-size: 15px;
  font-weight: 600;
}

.action-divider {
  width: 100%;
  height: 1px;
  background: rgba(0, 0, 0, 0.12);
  margin: 25px 0;
  align-self: center;
  border-radius: 1px;
}

.commerce-status-dot {
  min-width: 18px;
  padding: 0 4px;
}

.commerce-connect-btn {
  text-transform: none !important;
  font-weight: 600 !important;
}

@media (max-width: 767px) {
  .commerce-connect-btn {
    min-height: 44px;
  }
}
</style>
